// encryption.helper.ts
import { environment } from 'src/environments/environment';
import * as bcrypt from 'bcryptjs';

export class EncryptionHelper {
  private static readonly ENCRYPTION_KEY = environment.apiKey;

  
  /**
   * Encripta los datos usando Base64 con una clave simple
   * Para mayor seguridad, considera usar librerías como crypto-js
   */
  static encrypt(data: any): string {
    try {
      const jsonString = JSON.stringify(data);
      const encoded = btoa(unescape(encodeURIComponent(jsonString + EncryptionHelper.ENCRYPTION_KEY)));
      return encoded;
    } catch (error) {
      console.error('Error al encriptar datos:', error);
      return '';
    }
  }

  /**
   * Desencripta los datos
   */
  static decrypt(encryptedData: string): any {
    try {
      const decoded = decodeURIComponent(escape(atob(encryptedData)));
      const jsonString = decoded.replace(EncryptionHelper.ENCRYPTION_KEY, '');
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Error al desencriptar datos:', error);
      return null;
    }
  }

  /**
   * Encripta y guarda datos en localStorage
   */
  setEncryptedItem(key: string, data: any): void {
    try {
      const encryptedData = EncryptionHelper.encrypt(data);
      localStorage.setItem(key, encryptedData);
    } catch (error) {
      console.error('Error al guardar datos encriptados:', error);
    }
  }

  /**
   * Recupera y desencripta datos del localStorage
   */
  getDecryptedItem(key: string): any {
    try {
      const encryptedData = localStorage.getItem(key);
      if (!encryptedData) {
        return null;
      }
      return EncryptionHelper.decrypt(encryptedData);
    } catch (error) {
      console.error('Error al recuperar datos encriptados:', error);
      return null;
    }
  }

  /**
   * Crea y guarda un timestamp encriptado de la sesión actual
   */
  static createSessionTimestamp(): void {
    const timestamp = Date.now().toString();
    const encryptedTimestamp = this.encrypt(timestamp);
    localStorage.setItem('session_timestamp', encryptedTimestamp);
  }


  /**
   * Elimina un item del localStorage
   */
  removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * Verifica si existe un item en localStorage
   */
  hasItem(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }

 /**
   * Verifica un objeto contra un hash bcrypt
   */
  static verifyHash(obj: any, hash: string): boolean {
    try {
      const canonical = this.canonicalize(obj);
      return bcrypt.compareSync(canonical, hash);
    } catch {
      return false;
    }
  }
  /**
   * Crea un hash bcrypt de un objeto
   */
  static createHash(obj: any): string {
    const canonical = this.canonicalize(obj);
    return bcrypt.hashSync(canonical, 10);
  }


  /**
   * Limpia todas las sesiones almacenadas
   */
  static clearSessions(): void {
    localStorage.removeItem('session');
    localStorage.removeItem('session_hash');
    localStorage.removeItem('session_timestamp');
  }


  /**
   * Verifica la integridad de las sesiones almacenadas
   * Compara la sesión normal con la hasheada usando bcrypt
   */
  static verifySessionIntegrity(): boolean {
    try {
      const normalSession = localStorage.getItem('session');
      const hashedSession = localStorage.getItem('session_hash');
      
      if (!normalSession || !hashedSession) return false;
      
      const session = JSON.parse(normalSession);
      return this.verifyHash(session, hashedSession);
    } catch {
      return false;
    }
  }

  /**
     * Crea un string canónico y estable del objeto para evitar diferencias de orden de claves
     */
    private static canonicalize(obj: any): string {
      const stable = (value: any): any => {
        if (Array.isArray(value)) return value.map(stable);
        if (value && typeof value === 'object') {
          const keys = Object.keys(value).sort();
          const out: any = {};
          for (const k of keys) out[k] = stable(value[k]);
          return out;
        }
        return value;
      };
      return JSON.stringify(stable(obj));
    }

    /**
     * Verifica si la sesión ha expirado (3 horas = 10800000 ms)
     */
    static isSessionExpired(): boolean {
      try {
        const encryptedTimestamp = localStorage.getItem('session_timestamp');
        if (!encryptedTimestamp) return true;
      
        const decryptedTimestamp = this.decrypt(encryptedTimestamp);
        const sessionTime = parseInt(decryptedTimestamp, 10);
        const currentTime = Date.now();
        const threeHoursInMs = 3 * 60 * 60 * 1000; // 3 horas en milisegundos
      
        return (currentTime - sessionTime) > threeHoursInMs;
      } catch {
        return true; // Si hay error al descifrar, considerar expirada
      }
    }

}
